import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { X, Phone, BadgeCheck, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import {
  isFirebasePhoneConfigured,
  resetFirebaseRecaptcha,
  sendFirebasePhoneCode,
} from "../lib/firebasePhone";
import { showPhoneBonusResultToast } from "../lib/phoneBonus";

function cleanUsPhoneInput(value) {
  let digits = value.replace(/\D/g, "");
  if (digits.length > 10 && digits.startsWith("1")) digits = digits.slice(1);
  return digits.slice(0, 10);
}

function formatUsPhone(digits) {
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const PHONE_RATE_LIMIT_MS = 15 * 60 * 1000;

function cooldownKey(phone) {
  return `sophia-phone-verify-cooldown:${phone}`;
}

function readPhoneCooldown(phone) {
  if (typeof window === "undefined" || !phone) return 0;
  const value = Number(window.localStorage.getItem(cooldownKey(phone)));
  return Number.isFinite(value) ? value : 0;
}

function savePhoneCooldown(phone, until) {
  if (typeof window === "undefined" || !phone) return;
  window.localStorage.setItem(cooldownKey(phone), String(until));
}

function formatWait(ms) {
  const minutes = Math.max(1, Math.ceil(ms / 60000));
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}

function firebaseErrorCode(error) {
  if (error?.code) return error.code;
  const match = String(error?.message || "").match(/\((auth\/[^)]+)\)/);
  return match?.[1] || "";
}

function formatPhoneError(error, waitText) {
  const code = firebaseErrorCode(error);
  if (code === "auth/too-many-requests") {
    return `Too many SMS requests. Please wait ${waitText} before sending another code, or contact support to verify your phone.`;
  }
  if (code === "auth/invalid-phone-number") return "Enter a valid 10-digit US phone number.";
  if (code === "auth/quota-exceeded") return "SMS verification is temporarily busy. Please try again later or contact support.";
  if (code === "auth/captcha-check-failed" || code === "auth/missing-app-credential") {
    return "Security check expired. Please close this popup and try again.";
  }
  if (code === "auth/network-request-failed") return "Network issue. Please check your connection and try again.";
  return formatApiError(error);
}

export default function VerifyPhoneDialog({ open, onClose }) {
  const { refreshUser, user } = useAuth();
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [now, setNow] = useState(Date.now());

  const close = useCallback(() => {
    resetFirebaseRecaptcha();
    setStep(1);
    setPhone("");
    setCode("");
    setConfirmationResult(null);
    setErr("");
    setCooldownUntil(0);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open || step !== 2 || !user?.phone_verified) return;
    showPhoneBonusResultToast(toast, user);
    close();
  }, [close, open, step, user]);

  const normalizedPhone = phone.length === 10 ? `+1${phone}` : "";
  const displayCustomerPhone = normalizedPhone ? `+1 ${formatUsPhone(phone)}` : "+1";
  const cooldownRemaining = Math.max(0, cooldownUntil - now);
  const cooldownText = formatWait(cooldownRemaining || PHONE_RATE_LIMIT_MS);

  useEffect(() => {
    if (!open || phone.length !== 10) {
      setCooldownUntil(0);
      return;
    }
    setCooldownUntil(readPhoneCooldown(`+1${phone}`));
  }, [open, phone]);

  useEffect(() => {
    if (!open || cooldownRemaining <= 0) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [cooldownRemaining, open]);

  if (!open) return null;

  const requestOtp = async (e) => {
    e.preventDefault();
    setErr("");
    if (phone.length !== 10) {
      setErr("Enter a 10-digit US phone number.");
      return;
    }
    if (!isFirebasePhoneConfigured()) {
      setErr("Phone verification is not connected. Please contact support.");
      return;
    }
    const savedCooldown = readPhoneCooldown(normalizedPhone);
    const remaining = savedCooldown - Date.now();
    if (remaining > 0) {
      setCooldownUntil(savedCooldown);
      setErr(`Too many SMS requests. Please wait ${formatWait(remaining)} before sending another code, or contact support.`);
      return;
    }
    setLoading(true);
    try {
      resetFirebaseRecaptcha();
      const confirmation = await sendFirebasePhoneCode(normalizedPhone);
      setConfirmationResult(confirmation);
      setStep(2);
      toast.success("Verification code sent");
    } catch (e) {
      resetFirebaseRecaptcha();
      if (firebaseErrorCode(e) === "auth/too-many-requests") {
        const until = Date.now() + PHONE_RATE_LIMIT_MS;
        savePhoneCooldown(normalizedPhone, until);
        setCooldownUntil(until);
      }
      setErr(formatPhoneError(e, cooldownText));
    } finally {
      setLoading(false);
    }
  };

  const confirmOtp = async (e) => {
    e.preventDefault();
    setErr("");
    if (!code) {
      setErr("Enter the 6-digit SMS code.");
      return;
    }
    if (!confirmationResult) {
      setErr("Request a new verification code first.");
      return;
    }
    setLoading(true);
    try {
      const credential = await confirmationResult.confirm(code);
      const idToken = await credential.user.getIdToken();
      const { data } = await api.post("/auth/phone/firebase", { phone: normalizedPhone, id_token: idToken });
      showPhoneBonusResultToast(toast, data);
      close();
      await refreshUser();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={close}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="verify-phone-dialog"
        className="w-full max-w-lg rounded-2xl border border-[#D4AF37]/30 bg-[#0A130E] p-7 shadow-2xl"
      >
        <div id="firebase-recaptcha-container" />
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 grid place-items-center">
              <Phone className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <div>
              <h3 className="font-display text-xl font-bold leading-tight">Verify phone number</h3>
              <p className="text-xs text-white/50">Verify once to claim $5 Freeplay for game loads</p>
            </div>
          </div>
          <button onClick={close} data-testid="verify-close" className="p-1 hover:bg-white/10 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        {step === 1 && (
          <form onSubmit={requestOtp} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">Phone number</label>
              <div className="flex rounded-lg border border-white/10 bg-black/40 focus-within:border-[#D4AF37]">
                <div className="grid place-items-center border-r border-white/10 px-4 py-3 font-bold text-[#D4AF37]">
                  +1
                </div>
                <input
                  type="tel"
                  required
                  value={formatUsPhone(phone)}
                  onChange={(e) => setPhone(cleanUsPhoneInput(e.target.value))}
                  placeholder="(555) 123-4567"
                  data-testid="verify-phone-input"
                  className="min-w-0 flex-1 bg-transparent px-4 py-3 outline-none"
                />
              </div>
              <p className="mt-2 text-xs text-white/40">
                US numbers only. Verified players get $5 Freeplay for game loads.
              </p>
            </div>

            {err && (
              <div data-testid="verify-error" className="text-sm text-red-400 bg-red-900/20 border border-red-800/40 rounded-lg p-3">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || cooldownRemaining > 0}
              data-testid="verify-request-otp"
              className="w-full py-3 bg-[#D4AF37] text-black font-bold rounded-full hover:-translate-y-0.5 hover:bg-[#F1C40F] transition-all disabled:opacity-50"
            >
              {loading ? "Sending..." : cooldownRemaining > 0 ? `Try again in ${cooldownText}` : "Send verification code"}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={confirmOtp} className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#D4AF37]/15 border border-[#D4AF37]/40 grid place-items-center">
                <MessageCircle className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="font-display text-xl font-bold leading-tight">Enter SMS code</h3>
                <p className="text-sm text-white/50">Code sent to {displayCustomerPhone}</p>
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">6-digit code</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                data-testid="verify-code-input"
                className="w-full px-4 py-3 rounded-lg bg-black/40 border border-white/10 focus:border-[#D4AF37] focus:outline-none text-center text-2xl tracking-[0.5em] font-mono"
              />
            </div>

            {err && (
              <div data-testid="verify-error" className="text-sm text-red-400 bg-red-900/20 border border-red-800/40 rounded-lg p-3">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="verify-confirm-otp"
              className="w-full py-3 bg-[#D4AF37] text-black font-bold rounded-full hover:-translate-y-0.5 hover:bg-[#F1C40F] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <BadgeCheck className="w-4 h-4" /> {loading ? "Verifying..." : "Verify phone"}
            </button>
          </form>
        )}

        <p className="mt-6 text-[10px] text-white/30 text-center leading-relaxed">
          Your phone verifies only after the SMS code confirms the same phone number.
        </p>
      </div>
    </div>
  );
}
